import { useEffect, useRef, useState } from 'react';

/** The scroll container the element actually moves in. rootMargin is
 * measured against the observer's root, so prefetch-before-visible only
 * works if that root is the clipping scroller, not the window. */
const scrollParent = (from: HTMLElement): HTMLElement | null => {
  let node = from.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

/** True once the element has scrolled into view, and forever after — a
 * latch, so data fetched for it is never torn down by scrolling away.
 * `margin` starts the load that far before the element actually shows. */
export const useVisibleOnce = <T extends HTMLElement>(margin = '120px') => {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry?.isIntersecting && setVisible(true),
      { root: scrollParent(ref.current), rootMargin: margin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible, margin]);

  return { ref, visible };
};
